import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, EyeOff, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { toast } from "sonner";

/**
 * PapersViewer: opens papers in a modal with:
 *  - Visible dynamic watermark (user email + timestamp)
 *  - Right-click, drag, selection disabled
 *  - Blur on window blur / tab hidden (screenshot deterrent)
 *  - PrintScreen key detection -> blur & toast
 *  - No download button
 */
export default function PapersViewer({ open, onOpenChange, paths = [] }) {
  const { user } = useAuth();
  const { t } = useLang();
  const [idx, setIdx] = useState(0);
  const [blobs, setBlobs] = useState({});
  const [types, setTypes] = useState({});
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setIdx(0);
  }, [open]);

  useEffect(() => {
    if (!open || !user) return;
    const path = paths[idx];
    if (!path || blobs[path]) return;

    let cancelled = false;
    setLoading(true);
    const token = localStorage.getItem("bb_token");
    fetch(`${API}/files/${path}`, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(async (r) => {
        if (!r.ok) throw new Error("Failed");
        const ct = r.headers.get("Content-Type") || "application/octet-stream";
        const blob = await r.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        setBlobs((b) => ({ ...b, [path]: url }));
        setTypes((t) => ({ ...t, [path]: ct }));
      })
      .catch(() => toast.error(t.papers.loading))
      .finally(() => !cancelled && setLoading(false));

    return () => { cancelled = true; };
  }, [open, idx, paths, user, blobs, t]);

  // Blur on window blur / visibility change (screenshot deterrent)
  useEffect(() => {
    if (!open) return;
    const onBlur = () => document.body.classList.add("paper-hidden");
    const onFocus = () => document.body.classList.remove("paper-hidden");
    const onVis = () => {
      if (document.hidden) document.body.classList.add("paper-hidden");
      else document.body.classList.remove("paper-hidden");
    };
    const onKey = (e) => {
      if (e.key === "PrintScreen" || (e.ctrlKey && e.key.toLowerCase() === "p") || (e.metaKey && (e.key === "s" || e.key === "p"))) {
        e.preventDefault();
        document.body.classList.add("paper-hidden");
        toast.warning(t.papers.cantDownload);
        setTimeout(() => document.body.classList.remove("paper-hidden"), 1500);
      }
    };
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("keydown", onKey);
      document.body.classList.remove("paper-hidden");
    };
  }, [open, t]);

  // Cleanup blob URLs
  useEffect(() => {
    return () => {
      Object.values(blobs).forEach((u) => URL.revokeObjectURL(u));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent data-testid="papers-viewer-login-required">
          <DialogHeader>
            <DialogTitle>{t.papers.viewer}</DialogTitle>
          </DialogHeader>
          <div className="py-6 text-center text-sm text-muted-foreground">
            {t.papers.loginRequired}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const currentPath = paths[idx];
  const currentUrl = currentPath ? blobs[currentPath] : null;
  const currentType = currentPath ? types[currentPath] : null;
  const stamp = new Date().toLocaleString();
  const watermarkText = `${user.email} • ${stamp}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl p-0 overflow-hidden"
        onContextMenu={(e) => e.preventDefault()}
        data-testid="papers-viewer-dialog"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <ShieldCheck size={18} className="text-primary" /> {t.papers.viewer}
          </DialogTitle>
          <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1">
            <EyeOff size={12} /> {t.papers.secure}
          </p>
        </DialogHeader>

        <div className="flex items-center gap-2 px-5 py-2 border-b border-border overflow-x-auto">
          {paths.map((p, i) => (
            <button
              key={p}
              onClick={() => setIdx(i)}
              className={`text-xs px-3 py-1.5 rounded-full whitespace-nowrap ${
                i === idx ? "bg-primary text-primary-foreground" : "bg-accent text-foreground hover:bg-muted"
              }`}
              data-testid={`paper-tab-${i}`}
            >
              Paper #{i + 1}
            </button>
          ))}
        </div>

        <div
          ref={containerRef}
          className="paper-protect paper-blur-on-blur relative bg-muted min-h-[60vh] max-h-[70vh] overflow-hidden select-none"
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
        >
          {loading && (
            <div className="absolute inset-0 grid place-items-center text-sm text-muted-foreground">
              <Loader2 className="animate-spin" size={20} />
            </div>
          )}
          {currentUrl && currentType?.includes("pdf") ? (
            <iframe
              src={`${currentUrl}#toolbar=0&navpanes=0&scrollbar=0`}
              title="paper"
              className="w-full h-[70vh] bg-white"
              style={{ pointerEvents: "auto" }}
            />
          ) : currentUrl ? (
            <img
              src={currentUrl}
              alt="paper"
              className="w-full h-full object-contain max-h-[70vh]"
              draggable={false}
            />
          ) : null}
          <div className="watermark-overlay flex items-center justify-center">
            <div className="rotate-[-20deg] grid grid-cols-2 gap-16 opacity-40 text-primary font-bold text-lg select-none pointer-events-none">
              {Array.from({ length: 12 }).map((_, i) => (
                <span key={i} className="whitespace-nowrap">{watermarkText}</span>
              ))}
            </div>
          </div>
          <div className="absolute top-3 right-3 z-10 text-[10px] bg-black/70 text-white px-2 py-1 rounded" data-testid="paper-watermark-corner">
            {user.email}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
